import { useContext, useEffect, useRef, useState } from "react";

import { useGetCasinoGamesInfiniteQuery } from "@api";
import { MainContext } from "@context";
import { debounce } from "@utils";

const PAGE_SIZE = 42;

const useController = ({ onSearch, activeTab, onSearchToggle }) => {
	const searchInputRef = useRef(null);

	const [isLive, setIsLive] = useState(false);
	const [isToggled, setIsToggled] = useState(false);
	const [searchValue, setSearchValue] = useState("");

	const { state: contextState } = useContext(MainContext);

	const {
		data: casino,
		isLoading,
		isFetching,
	} = useGetCasinoGamesInfiniteQuery(
		{
			size: PAGE_SIZE,
			search: searchValue,
			category: "search",
			is_live: isLive,
			lang: "en",
			isSearch: true,
		},
		{ skip: searchValue === "" || searchValue?.length < 4 }
	);

	const casinoGames = casino?.pages?.[0]?.games || [];

	const debouncedSearch = debounce((value) => {
		if (value?.length > 4 && !isLoading && !isFetching) {
			setSearchValue(value);
			onSearch?.(value);
		}
	}, 700);

	const handleToggleChange = () => {
		setIsToggled((prev) => !prev);
		onSearchToggle?.(!isToggled);
	};

	const handleLiveToggle = (value) => {
		setIsLive(value);
	};

	const handleSearchInput = (e) => {
		const value = e?.target?.value;
		debouncedSearch(value);
	};

	const handleClearInput = () => {
		setSearchValue("");

		if (searchInputRef.current) {
			searchInputRef.current.value = "";
			searchInputRef.current.focus();
		}
	};

	useEffect(() => {
		const handleKeyDown = (e) => {
			if (e.key === "Escape") {
				handleToggleChange();
			}
		};

		if (isToggled) {
			searchInputRef.current.focus();
			window.addEventListener("keydown", handleKeyDown);
		} else {
			handleClearInput();
			window.removeEventListener("keydown", handleKeyDown);
		}
		return () => {
			handleClearInput();
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, [isToggled]);

	useEffect(() => {
		if (activeTab === "Casino") {
			setIsLive(false);
		} else if (activeTab === "LiveCasino") {
			setIsLive(true);
		}
	}, [activeTab]);

	return {
		refs: { searchInputRef },
		state: {
			...contextState,
			isDesktop: contextState.isDesktop || contextState.isWide || contextState.isUltraWide,
			gameList: casinoGames,
			isLoading: isLoading || isFetching,
			isToggled,
			isLive,
			searchValue,
		},
		handlers: { handleToggleChange, handleLiveToggle, handleSearchInput, handleClearInput },
	};
};

export default useController;
